import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadTtpReport, ttpReportError } from "../../api/ttpReports";
import { showErrorMessage } from "../../helper/helper";
import { DocumentIcon, SpreadsheetIcon, PalmIcon, TableIcon } from "./PurchaseReportIcons";

type ExportContext = {
    millId: number
    /** YYYY-MM-DD, sent to the API. */
    from: string
    to: string
    /** "YYYY_MM_DD.YYYY_MM_DD", used in the fallback file names. */
    fileStamp: string
    /** PNG of the purchase map, or null when it cannot be captured. */
    captureMap: () => Promise<Blob | null>
}

type PurchaseExport = {
    id: string
    titleKey: string
    descKey: string
    format: string
    icon: React.ReactNode
    run: (ctx: ExportContext) => Promise<void>
}

/** The monthly report endpoint rejects map images above 5 MB. */
const MAX_MAP_PNG_BYTES = 5 * 1024 * 1024

const params = ({ millId, from, to }: ExportContext) => ({ mill: millId, from, to })

/**
 * The exports offered by the report. Every file is generated by the TTP report
 * API; the fallback names are used when it sends no Content-Disposition.
 */
export const PURCHASE_EXPORTS: PurchaseExport[] = [
    {
        id: 'tpp_csv',
        titleKey: 'export_tpp_csv',
        descKey: 'export_tpp_csv_desc',
        format: 'CSV',
        icon: <TableIcon size={17} color="var(--color-primary)" />,
        run: ctx => downloadTtpReport('ttp.csv', params(ctx), `pempem.${ctx.fileStamp}.traceability.csv`),
    },
    {
        id: 'fruit_source',
        titleKey: 'export_fruit_source',
        descKey: 'export_fruit_source_desc',
        format: 'CSV',
        icon: <PalmIcon size={17} color="var(--color-primary)" />,
        run: ctx => downloadTtpReport('fruit-sources.csv', params(ctx), `pempem.${ctx.fileStamp}.fruit_source.csv`),
    },
    {
        id: 'astra_ttp_v2',
        titleKey: 'export_astra_ttp_v2',
        descKey: 'export_astra_ttp_v2_desc',
        format: 'CSV',
        icon: <SpreadsheetIcon size={17} color="var(--color-primary)" />,
        run: ctx => downloadTtpReport('astra.csv', params(ctx), `pempem.${ctx.fileStamp}.astra_ttp_v2.csv`),
    },
    {
        id: 'astra_report',
        titleKey: 'export_astra_report',
        descKey: 'export_astra_report_desc',
        format: 'PDF',
        icon: <DocumentIcon size={17} color="var(--color-primary)" />,
        run: ctx => downloadTtpReport('astra.pdf', params(ctx), `pempem.${ctx.fileStamp}.astra_report.pdf`),
    },
    {
        id: 'monthly_report',
        titleKey: 'export_monthly_report',
        descKey: 'export_monthly_report_desc',
        format: 'PDF',
        icon: <DocumentIcon size={17} color="var(--color-primary)" />,
        run: async ctx => {
            // The map is optional: without it the API still renders the report.
            const png = await ctx.captureMap().catch(() => null)
            const body = png && png.size <= MAX_MAP_PNG_BYTES ? png : undefined
            await downloadTtpReport('monthly.pdf', params(ctx), `pempem.${ctx.fileStamp}.monthly_report.pdf`, body)
        },
    },
]

type Props = ExportContext & {
    isDisable?: boolean
    hasTtpDataAccess?: boolean
}

export const PurchaseExportPanel: React.FC<Props> = ({ isDisable, hasTtpDataAccess, ...ctx }) => {
    const { t } = useTranslation()
    const [busyId, setBusyId] = useState<string | null>(null)
    // Mills without TTP data access in Sarana see the exports locked, not hidden.
    const isLocked = !hasTtpDataAccess

    const onExport = async (exp: PurchaseExport) => {
        setBusyId(exp.id)
        try {
            await exp.run(ctx)
        } catch (e) {
            showErrorMessage(await ttpReportError(e))
        } finally {
            setBusyId(null)
        }
    }

    return (
        <div className="purchase-report__exports">
            <div className="ds-rule">
                <span className="ds-rule__label">{t('choose_an_export')}</span>
            </div>
            <div className="purchase-report__export-lock">
                <div className="ds-export-grid" aria-hidden={isLocked}>
                    {PURCHASE_EXPORTS.map(exp => (
                        <button
                            key={exp.id}
                            type="button"
                            className="ds-export-card"
                            disabled={isLocked || isDisable || !ctx.millId || busyId === exp.id}
                            onClick={() => onExport(exp)}
                        >
                            <span className="ds-export-card__icon">{exp.icon}</span>
                            <span className="ds-export-card__body">
                                <span className="ds-export-card__title">{t(exp.titleKey)}</span>
                                <span className="ds-export-card__desc">{t(exp.descKey)}</span>
                                <span className="ds-export-card__format">{exp.format}</span>
                            </span>
                        </button>
                    ))}
                </div>
                {isLocked && (
                    <div className="purchase-report__export-overlay" role="status">
                        <span className="purchase-report__export-overlay-msg">{t('ttp_data_access_required')}</span>
                    </div>
                )}
            </div>
        </div>
    )
}
